import { DateTime } from '@ksyos/date-time';
import {
    Icon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import type { IEpisodeCategory } from '@common/interfaces/episodeCategory';
import type { ITimeRange } from '@common/validation/callBackMoment';
import type { ITimeSlots } from '@common/validation/timeSlots';
import type { IPatientTask } from '@frontend/components/interfaces';
import { translateKey } from '@frontend/translate';
import { getPatientTaskDescriptionOrType } from '@frontend/utils/getPatientTaskDescriptionOrType';
import { generateUrlForRoute } from '@frontend/utils/hooks/useNavigateTo';
import type { RequestListHeader } from './RequestsList';
import { FullName } from '../../../elements/common';
import { DisplayDatePassed } from '../../../elements/DisplayDatePassed';

export type PatientTaskListHeader =
    | 'expectedDateCompleted'
    | 'availability'
    | 'description'
    | 'patientName'
    | 'birthDate'
    | 'performerPractitioner'
    | 'sortDate';
export type SortDirection = 'ASC' | 'DESC';

interface IPatientTasksListProps {
    onSort: (sortBy: PatientTaskListHeader) => Promise<void>;
    patientTasks: IPatientTask[];
    sortableHeaders?: PatientTaskListHeader[];
    sortBy?: RequestListHeader | PatientTaskListHeader;
    sortDirection?: SortDirection;
}

export function PatientTasksList(props: IPatientTasksListProps) {
    const navigate = useNavigate();
    if (!props.patientTasks || props.patientTasks.length === 0) {
        return null;
    }

    const headers: { key: PatientTaskListHeader; label: string }[] = [
        { key: 'expectedDateCompleted', label: translateKey('contactsAndTasks.expectedDateCompleted') },
        { key: 'availability', label: translateKey('contactsAndTasks.availability') },
        { key: 'description', label: translateKey('contactsAndTasks.patientTaskList.description') },
        { key: 'patientName', label: translateKey('contactsAndTasks.patientTaskList.patientName') },
        { key: 'birthDate', label: translateKey('contactsAndTasks.patientTaskList.birthDate') },
        { key: 'performerPractitioner', label: translateKey('contactsAndTasks.patientTaskList.performerPractitioner') },
        { key: 'sortDate', label: translateKey('contactsAndTasks.patientTaskList.sortDate') },
    ];

    return (
        <TableContainer>
            <Table spacing="medium">
                <TableHead>
                    <TableRow>
                        {headers.map((header) => (
                            <TableHeader
                                isSortable={props.sortableHeaders?.includes(header.key)}
                                isSortHeader={props.sortBy === header.key}
                                key={header.key}
                                onClick={() => props.onSort(header.key)}
                                sortDirection={props.sortBy === header.key ? props.sortDirection : undefined}
                            >
                                {header.label}
                            </TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.patientTasks.map((patientTask: IPatientTask) => {
                        const isAfterDeadline = DateTime.from(patientTask.expectedDateCompleted).isBefore(new Date());

                        return (
                            <TableRow
                                key={patientTask.id}
                                onClick={() => navigate(computeHref(patientTask))}
                            >
                                <TableCell>
                                    <Text
                                        as="span"
                                        color={isAfterDeadline ? 'danger' : 'default'}
                                        size="sm"
                                    >
                                        <DisplayDatePassed
                                            className="d-inline-block"
                                            isTimeHidden
                                            style={{ width: '85px' }}
                                            value={patientTask.expectedDateCompleted}
                                        />
                                    </Text>
                                    {isAfterDeadline && (
                                        <Icon
                                            className="d-inline ml-4"
                                            color="warning"
                                            icon="warning"
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {patientTask.customControlData ? (
                                        <>
                                            {patientTask.customControlData.availability.firstTimeRange.startTime ||
                                            patientTask.customControlData.availability.secondTimeRange.startTime
                                                ? renderAvailabilityInformation(
                                                      patientTask.customControlData.availability
                                                  )
                                                : '-'}
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    {getPatientTaskDescriptionOrType(
                                        patientTask,
                                        patientTask.episodeCategory as IEpisodeCategory
                                    )}
                                </TableCell>
                                <TableCell>
                                    <FullName {...patientTask.patient} />
                                </TableCell>
                                <TableCell>
                                    <DisplayDatePassed
                                        isTimeHidden
                                        value={patientTask.patient.birthDate}
                                    />
                                </TableCell>
                                <TableCell>
                                    {' '}
                                    {patientTask.performerPractitionerProfession ? (
                                        <>
                                            <b>
                                                {patientTask.performerPractitioner.nameFamily ? (
                                                    <FullName {...patientTask.performerPractitioner} />
                                                ) : (
                                                    '-'
                                                )}{' '}
                                            </b>
                                            <br />
                                            {translateKey(
                                                `professionEnum.${patientTask.performerPractitionerProfession}`
                                            )}
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                                <TableCell>
                                    <DisplayDatePassed
                                        isTimeHidden
                                        value={patientTask.updated_at}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function computeHref(patientTask: IPatientTask): string {
        if (!patientTask.episodeCategory) {
            throw new Error('Expected episode category for patient task');
        }

        const episodeCategory = patientTask.episodeCategory.name;
        const itemId = patientTask.id;
        const patientId = patientTask.patient.id;

        if (patientTask?.isFlexibleCarePath) {
            return generateUrlForRoute(
                'patientCarePath',
                {
                    activeTab: 'support',
                    episodeCategory,
                    patientId,
                },
                { itemId }
            );
        }

        return `/contactsAndTasks?patientId=${patientId}&episodeCategory=${episodeCategory}&itemId=${itemId}`;
    }

    function renderTimeRangeInformation(timeRange: ITimeRange) {
        return (
            <div>
                {timeRange.startTime}
                {' - '}
                {timeRange.endTime}
            </div>
        );
    }

    function renderAvailabilityInformation(timeSlots: ITimeSlots) {
        return (
            <>
                {timeSlots.firstTimeRange.startTime && renderTimeRangeInformation(timeSlots.firstTimeRange)}
                {timeSlots.secondTimeRange.startTime && renderTimeRangeInformation(timeSlots.secondTimeRange)}
            </>
        );
    }
}
